import { useMemo } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding, useProperty } from '@vibesites/runtime/react';
import pagesData from '../pages.json';

interface PageEntry {
  name: string;
  path: string;
  position: number;
  visible?: boolean;
  template?: string;
  parent?: string;
}

interface VirtualTour {
  virtual_tour_url?: string | null;
}

/** Same alias denylist as Header.buildNavLinks — keep in sync. */
const NAV_EXCLUDED_ALIASES = new Set([
  'home',
  'privacy-policy',
  'sitemap',
  'accessibility',
  'apply-now',
  'thank-you',
  'resident-access',
  'schedule-a-tour',
  'hyperlocal',
  'reviews',
]);

/**
 * Pages listed on the sitemap: Home (always, even though it is hidden from
 * primary nav) plus the same visible pages as Header.buildNavLinks.
 * Flat list sorted by position — nested nav children still list here.
 */
function buildSitemapPages(
  hasVirtualTours: boolean,
  hasInteractiveLeasing: boolean,
): Array<{ name: string; path: string }> {
  return Object.entries(pagesData as Record<string, PageEntry>)
    .filter(([alias, page]) => {
      if (alias === 'home') return true;
      if (NAV_EXCLUDED_ALIASES.has(alias)) return false;
      if (page.visible === false) return false;
      if (page.template) return false;
      if (alias === 'virtual-tour' && !hasVirtualTours) return false;
      if (alias === 'interactive-map' && !hasInteractiveLeasing) return false;
      return true;
    })
    .sort(([, a], [, b]) => a.position - b.position)
    .map(([, page]) => ({ name: page.name, path: page.path }));
}

export function SitemapContent() {
  const virtualTours = useCollectionBinding<VirtualTour>('sitemapcontent.virtual_tours', {
    collection: 'virtual_tours',
  });
  const hasVirtualTours = (virtualTours || []).some((t) => t.virtual_tour_url);

  const propertyForLeasing = useProperty() as Record<string, unknown>;
  const apartmentSyncJsId = propertyForLeasing?.custom_apartmentsync_javascript_id as
    | string
    | null
    | undefined;
  const apartmentSyncConfigId = propertyForLeasing?.custom_apartmentsync_config_id as
    | string
    | null
    | undefined;
  const hasInteractiveLeasing =
    !!(apartmentSyncJsId && String(apartmentSyncJsId).trim()) &&
    !!(apartmentSyncConfigId && String(apartmentSyncConfigId).trim());

  const pages = useMemo(
    () => buildSitemapPages(hasVirtualTours, hasInteractiveLeasing),
    [hasVirtualTours, hasInteractiveLeasing],
  );

  return (
    <section className="sitemap-section">
      <div className="sitemap-inner">
        <ul className="sitemap-list">
          {pages.map((page) => (
            <li key={page.path} className="sitemap-item">
              <a href={page.path} className="sitemap-link">
                {page.name}
              </a>
            </li>
          ))}
        </ul>
        {/* Closing divider under the last row so the list reads as
            a full stack of framed rows, matching the reference. */}
        <div className="sitemap-list-end" aria-hidden="true" />
      </div>

      <style>{`
        /* Sitemap page: centered stack of framed link rows. */
        .sitemap-section {
          background-color: var(--vibe-color-background);
          padding: 4.5rem 1.25rem 5rem;
        }
        @media (max-width: 640px) {
          .sitemap-section {
            padding: 3rem 1rem 3.25rem;
          }
        }
        .sitemap-inner {
          max-width: 1180px;
          margin: 0 auto;
        }
        /* Stacked centered rows with hairline dividers between each,
           mimicking the framed link list from the reference screenshot.
           List is intentionally narrow so the divider lines only span
           roughly the width of the link text, not the full page. */
        .sitemap-list {
          list-style: none;
          padding: 0;
          margin: 0 auto;
          max-width: 16rem;
          color: var(--vibe-color-text);
        }
        .sitemap-item {
          border-top: 1px solid var(--vibe-color-border);
          text-align: center;
        }
        /* No divider above the very first row — only between and below. */
        .sitemap-item:first-child {
          border-top: none;
        }
        .sitemap-list-end {
          max-width: 16rem;
          margin: 0 auto;
          border-top: 1px solid var(--vibe-color-border);
        }
        .sitemap-link {
          display: block;
          padding: 1.35rem 1rem;
          font-family: var(--vibe-font-heading);
          font-size: clamp(1.5rem, 2.6vw, 2rem);
          font-weight: 300;
          letter-spacing: 0.005em;
          line-height: 1.2;
          color: var(--vibe-color-text);
          text-decoration: none;
          transition: color var(--vibe-transition-fast, 150ms ease);
        }
        .sitemap-link:hover,
        .sitemap-link:focus-visible {
          color: var(--vibe-color-text-muted);
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'SitemapContent',
  displayName: 'Sitemap',
  tokens: {},
  collectionBindings: {
    'sitemapcontent.virtual_tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
  },
};

(SitemapContent as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default SitemapContent;
